import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Handles the return from the app's OWN Google OAuth client.
 * Google redirects to /auth/google?code=...&state=... — we exchange the code
 * (server-side) for our app session, then route home.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleOAuthCallback() {
    const { googleOAuthCallback } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const params = new URLSearchParams(location.search || "");
        const code = params.get("code");
        const state = params.get("state");
        const err = params.get("error");
        let savedState = null;
        try { savedState = sessionStorage.getItem("g_oauth_state"); sessionStorage.removeItem("g_oauth_state"); } catch { /* ignore */ }
        const redirectUri = window.location.origin + "/auth/google";
        // Clean the code/state out of the URL history.
        window.history.replaceState(null, "", window.location.pathname);
        if (err || !code) { toast.error("Google sign-in was cancelled"); navigate("/login", { replace: true }); return; }
        if (!state || state !== savedState) { toast.error("Google sign-in failed (state mismatch)"); navigate("/login", { replace: true }); return; }
        (async () => {
            const res = await googleOAuthCallback(code, redirectUri);
            if (res.ok) {
                toast.success(`Welcome${res.user?.name ? ", " + res.user.name.split(" ")[0] : ""}!`);
                navigate("/", { replace: true });
            } else {
                toast.error(res.error || "Google sign-in failed");
                navigate("/login", { replace: true });
            }
        })();
    }, [googleOAuthCallback, navigate, location.search]);

    return (
        <div className="min-h-[60vh] flex items-center justify-center text-[#a0a0a0]" data-testid="google-oauth-callback">
            <div className="text-center">
                <div className="h-10 w-10 border-2 border-[#ff4500] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <div className="text-sm uppercase tracking-[0.2em]">Signing you in…</div>
            </div>
        </div>
    );
}
